import { useEffect, useRef } from "react";

interface LatLng { lat: number; lng: number }

interface Props {
  pickup: LatLng;
  dropoff: LatLng;
  polyline?: string;
  pickupLabel?: string;
  dropoffLabel?: string;
  className?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GMaps = any;
declare global {
  interface Window {
    google?: { maps: GMaps };
    __bachaInitMap?: () => void;
    __bachaMapsLoading?: Promise<void>;
  }
}

function loadMapsScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (window.__bachaMapsLoading) return window.__bachaMapsLoading;

  // The Lovable-managed key is restricted to *.lovable.app and will throw
  // RefererNotAllowedMapError on custom domains. Use the project's own key
  // (restricted to bachawaytravel.com) whenever we're not on a lovable.app host.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLovableHost = /(^|\.)lovable\.app$/.test(host) || host === "localhost" || host === "127.0.0.1";
  const customKey = "AIzaSyAoeAGqB9RSQJ4wWwGLj1VVIXbxPhLoRGw";
  const managedKey = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY;
  const key = isLovableHost ? (managedKey || customKey) : customKey;
  const channel = isLovableHost ? import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_TRACKING_ID : undefined;

  window.__bachaMapsLoading = new Promise<void>((resolve, reject) => {
    window.__bachaInitMap = () => resolve();
    const s = document.createElement("script");
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async&libraries=geometry&callback=__bachaInitMap${channel ? `&channel=${channel}` : ""}`;
    s.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(s);
  });
  return window.__bachaMapsLoading;
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

export function RouteMap({ pickup, dropoff, polyline, pickupLabel, dropoffLabel, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMapsScript()
      .then(() => {
        if (cancelled || !ref.current || !window.google?.maps) return;
        const g = window.google.maps;
        const map = new g.Map(ref.current, {
          center: { lat: (pickup.lat + dropoff.lat) / 2, lng: (pickup.lng + dropoff.lng) / 2 },
          zoom: 7,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          styles: [
            { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
            { featureType: "poi", stylers: [{ visibility: "off" }] },
          ],
        });

        const bounds = new g.LatLngBounds();
        bounds.extend(pickup);
        bounds.extend(dropoff);

        const pickupMarker = new g.Marker({ position: pickup, map, label: "A", title: pickupLabel ?? "Pickup" });
        const dropoffMarker = new g.Marker({ position: dropoff, map, label: "B", title: dropoffLabel ?? "Dropoff" });

        const labelStyle = "font-family:'Fira Sans',system-ui,sans-serif;font-size:12px;color:#0f1b3d;font-weight:600;padding:2px 4px;";
        if (pickupLabel) {
          new g.InfoWindow({
            content: `<div style="${labelStyle}">${escapeHtml(pickupLabel)}</div>`,
            disableAutoPan: true,
          }).open({ anchor: pickupMarker, map, shouldFocus: false });
        }
        if (dropoffLabel) {
          new g.InfoWindow({
            content: `<div style="${labelStyle}">${escapeHtml(dropoffLabel)}</div>`,
            disableAutoPan: true,
          }).open({ anchor: dropoffMarker, map, shouldFocus: false });
        }

        let path: Array<{ lat: () => number; lng: () => number }> | null = null;
        if (polyline && g.geometry?.encoding) {
          path = g.geometry.encoding.decodePath(polyline);
        }

        let animationPath: Array<{ lat: number; lng: number }>;
        if (path && path.length > 1) {
          new g.Polyline({
            path,
            map,
            strokeColor: "#1e2a78",
            strokeOpacity: 0.9,
            strokeWeight: 4,
          });
          animationPath = path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
          path.forEach((p) => bounds.extend(p));
        } else {
          new g.Polyline({
            path: [pickup, dropoff],
            map,
            strokeColor: "#1e2a78",
            strokeOpacity: 0.6,
            strokeWeight: 3,
            icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 }, offset: "0", repeat: "12px" }],
          });
          animationPath = [pickup, dropoff];
        }

        map.fitBounds(bounds, 48);

        // Animated route tracker — moving marker along the polyline.
        if (animationPath.length > 1 && g.geometry?.spherical) {
          const sph = g.geometry.spherical;
          const segLengths: number[] = [];
          let total = 0;
          for (let i = 0; i < animationPath.length - 1; i++) {
            const d = sph.computeDistanceBetween(animationPath[i], animationPath[i + 1]);
            segLengths.push(d);
            total += d;
          }

          const carIcon = {
            path: "M -4,-2 L 4,-2 L 5,0 L 4,2 L -4,2 L -5,0 z",
            scale: 1.4,
            fillColor: "#3b6fa0",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            rotation: 0,
            anchor: new g.Point(0, 0),
          };
          const moving = new g.Marker({
            position: animationPath[0],
            map,
            icon: carIcon,
            zIndex: 9999,
            clickable: false,
          });

          const DURATION_MS = 9000;
          let start: number | null = null;
          const step = (ts: number) => {
            if (cancelled) return;
            if (start === null) start = ts;
            const t = ((ts - start) % DURATION_MS) / DURATION_MS;
            let traveled = t * total;
            let i = 0;
            while (i < segLengths.length && traveled > segLengths[i]) {
              traveled -= segLengths[i];
              i++;
            }
            if (i >= segLengths.length) i = segLengths.length - 1;
            const frac = segLengths[i] > 0 ? traveled / segLengths[i] : 0;
            const pos = sph.interpolate(animationPath[i], animationPath[i + 1], frac);
            const heading = sph.computeHeading(animationPath[i], animationPath[i + 1]);
            moving.setPosition(pos);
            moving.setIcon({ ...carIcon, rotation: heading });
            rafRef.current = requestAnimationFrame(step);
          };
          rafRef.current = requestAnimationFrame(step);
        }
      })
      .catch(() => {
        if (ref.current) {
          ref.current.innerHTML =
            '<div class="flex h-full items-center justify-center text-sm text-muted-foreground">Map unavailable</div>';
        }
      });
    return () => {
      cancelled = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [pickup.lat, pickup.lng, dropoff.lat, dropoff.lng, polyline, pickupLabel, dropoffLabel]);

  return (
    <div
      ref={ref}
      role="img"
      aria-label={
        pickupLabel && dropoffLabel
          ? `Route map from ${pickupLabel} to ${dropoffLabel}`
          : "Route map showing pickup and dropoff locations"
      }
      className={className ?? "w-full h-72 rounded-xl overflow-hidden border border-border bg-muted"}
    />
  );
}
